import { ICinemaPalette, withAlpha } from './palette';

const MOBILE = '@media (max-width: 900px)';

const HALO = '0 1px 2px rgba(4,6,9,.55), 0 2px 22px rgba(4,6,9,.45)';
const SOFT_HALO = '0 1px 2px rgba(4,6,9,.5)';
const MONO = "'JetBrains Mono', monospace";
const DISP = "'Space Grotesk', sans-serif";
const BODY = "'Inter', sans-serif";
const INK_DIM = 'rgba(244,246,250,.72)';
const INK_FAINT = 'rgba(244,246,250,.5)';

export const getCinemaStyles = (palette: ICinemaPalette) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 30,
    overflow: 'hidden',
    fontFamily: BODY,
    color: '#f4f6fa',
    background: '#0b0e14',
  },
  media: {
    position: 'absolute',
    inset: 0,
    '& video, & img': {
      objectFit: 'cover',
      maxWidth: 'unset !important',
      width: '100% !important',
      height: '100% !important',
      display: 'flex',
    },
    '& iframe': {
      width: '100%',
      height: '100%',
    },
  },
  railBlur: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '38%',
    pointerEvents: 'none',
    backdropFilter: 'blur(26px) saturate(1.05)',
    WebkitBackdropFilter: 'blur(26px) saturate(1.05)',
    maskImage: 'linear-gradient(to top, #000 0%, #000 30%, transparent 100%)',
    WebkitMaskImage:
      'linear-gradient(to top, #000 0%, #000 30%, transparent 100%)',
  },
  railScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '46%',
    pointerEvents: 'none',
    background:
      'linear-gradient(to top, rgba(4,6,9,.92) 0%, rgba(4,6,9,.62) 38%, rgba(4,6,9,.22) 70%, transparent 100%)',
  },
  topBlur: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: '130px',
    pointerEvents: 'none',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    maskImage:
      'linear-gradient(to bottom, #000 0%, #000 20%, transparent 100%)',
    WebkitMaskImage:
      'linear-gradient(to bottom, #000 0%, #000 20%, transparent 100%)',
  },
  topScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: '160px',
    pointerEvents: 'none',
    background:
      'linear-gradient(to bottom, rgba(4,6,9,.82), rgba(4,6,9,.38) 55%, transparent)',
  },
  logoWrap: {
    position: 'absolute',
    top: '26px',
    left: '44px',
    zIndex: 15,
    [MOBILE]: { left: '20px' },
  },
  logoImg: {
    height: '28px',
    display: 'block',
    filter: 'drop-shadow(0 1px 3px rgba(4,6,9,.5))',
  },
  logoNode: {
    filter: 'drop-shadow(0 1px 3px rgba(4,6,9,.5))',
    '& img, & svg': {
      height: '28px',
      display: 'block',
    },
  },
  sys: {
    position: 'absolute',
    top: '22px',
    right: '24px',
    display: 'flex',
    gap: '10px',
    zIndex: 20,
  },
  sysBtn: {
    width: '40px',
    height: '40px',
    padding: 0,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,.18)',
    background: 'rgba(10,12,16,.5)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    color: 'rgba(255,255,255,.88)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all .15s',
    '&:hover': {
      background: 'rgba(10,12,16,.72)',
      borderColor: 'rgba(255,255,255,.32)',
    },
    '& svg': { width: '17px', height: '17px' },
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #7a5c36, #4d3a22)',
    border: '1px solid rgba(255,255,255,.22)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: DISP,
    fontSize: '13px',
    fontWeight: 600,
    color: '#fff',
  },
  left: {
    position: 'absolute',
    left: '44px',
    bottom: '56px',
    maxWidth: '620px',
    zIndex: 10,
    [MOBILE]: { left: '20px', bottom: '48px', right: '150px' },
  },
  // The spot placement notice. A glassy neutral pill, deliberately NOT the accent colour: the accent belongs to
  // the one thing on this screen the user can act on (the Enter CTA), and a note that needs no action must not
  // compete with it. Sits above the step line so the reading order is why-it-is-slow → what-is-happening-now.
  econ: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '15px',
    padding: '8px 14px 9px 12px',
    borderRadius: '14px',
    border: '1px solid rgba(244,246,250,.14)',
    background: 'rgba(8,11,16,.52)',
    backdropFilter: 'blur(14px) saturate(1.05)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.05)',
    boxShadow: SOFT_HALO,
    '& svg': {
      width: '16px',
      height: '16px',
      flex: 'none',
      marginTop: '2px',
      color: '#fff',
      opacity: 0.85,
      filter: 'drop-shadow(0 1px 2px rgba(4,6,9,.5))',
    },
  },
  econTag: {
    display: 'block',
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '.2em',
    textTransform: 'uppercase',
    color: '#fff',
    textShadow: HALO,
    marginBottom: '3px',
  },
  econText: {
    display: 'block',
    fontSize: '12.5px',
    lineHeight: 1.45,
    color: INK_DIM,
    textShadow: SOFT_HALO,
    '& b': { color: '#fff', fontWeight: 600 },
  },
  stepLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '11px',
    marginBottom: '13px',
  },
  stepDot: {
    width: '8px',
    height: '8px',
    flex: 'none',
    borderRadius: '50%',
    alignSelf: 'center',
    background: palette.accentHi,
    boxShadow: `0 0 10px 2px ${palette.accentGlow}`,
    animation: 'mytaCinemaPulse 1.7s ease-in-out infinite',
  },
  stepDotStatic: {
    animation: 'none',
  },
  stepName: {
    fontFamily: MONO,
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '.15em',
    textTransform: 'uppercase',
    color: '#fff',
    textShadow: HALO,
    whiteSpace: 'nowrap',
    transition: 'opacity .18s ease',
  },
  stepCnt: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '.08em',
    color: INK_FAINT,
    textShadow: SOFT_HALO,
    [MOBILE]: { display: 'none' },
  },
  headline: {
    fontFamily: DISP,
    fontWeight: 600,
    fontSize: '40px',
    letterSpacing: '-.02em',
    lineHeight: 1.06,
    color: '#fff',
    textShadow: HALO,
    [MOBILE]: { fontSize: '27px' },
  },
  hint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '9px',
    color: INK_DIM,
    fontSize: '12.5px',
    lineHeight: 1.5,
    marginTop: '14px',
    textShadow: SOFT_HALO,
    '& svg': {
      width: '16px',
      height: '16px',
      flex: 'none',
      marginTop: '1px',
      filter: 'drop-shadow(0 1px 2px rgba(4,6,9,.5))',
    },
  },
  right: {
    position: 'absolute',
    right: '44px',
    bottom: '52px',
    textAlign: 'right',
    zIndex: 10,
    [MOBILE]: { right: '20px' },
  },
  pct: {
    fontFamily: MONO,
    fontWeight: 600,
    color: '#fff',
    textShadow: HALO,
    fontSize: '56px',
    lineHeight: 1,
    letterSpacing: '-.03em',
    '& small': { fontSize: '26px', color: INK_DIM },
    [MOBILE]: { fontSize: '38px' },
  },
  eta: {
    marginTop: '8px',
    fontSize: '13px',
    color: INK_DIM,
    textShadow: SOFT_HALO,
    '& b': { fontFamily: MONO, color: '#fff', fontWeight: 600 },
  },
  dock: {
    position: 'absolute',
    left: '50%',
    bottom: '110px',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    zIndex: 14,
  },
  dockSlot: {
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  guide: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '11px',
    [MOBILE]: { display: 'none' },
  },
  guideKicker: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '.24em',
    textTransform: 'uppercase',
    color: INK_FAINT,
    textShadow: SOFT_HALO,
  },
  guideCard: {
    display: 'flex',
    alignItems: 'stretch',
    borderRadius: '18px',
    border: '1px solid rgba(255,255,255,.15)',
    background: 'rgba(8,10,15,.44)',
    backdropFilter: 'blur(18px) saturate(1.15)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.15)',
    boxShadow:
      '0 18px 50px rgba(4,6,9,.5), inset 0 1px 0 rgba(255,255,255,.08)',
    padding: '16px 6px 13px',
  },
  guideGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '0 26px',
  },
  guideGlyphs: {
    height: '65px',
    display: 'flex',
    alignItems: 'center',
  },
  guideLabel: {
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 500,
    letterSpacing: '.16em',
    textTransform: 'uppercase',
    color: INK_DIM,
    textShadow: SOFT_HALO,
    whiteSpace: 'nowrap',
  },
  guideDivider: {
    width: '1px',
    margin: '4px 0',
    background:
      'linear-gradient(180deg, transparent, rgba(255,255,255,.18), transparent)',
  },
  wasdGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 30px)',
    gap: '5px',
  },
  keycap: {
    height: '30px',
    minWidth: '30px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,.28)',
    background:
      'linear-gradient(180deg, rgba(255,255,255,.17), rgba(255,255,255,.05))',
    boxShadow: '0 2px 0 rgba(4,6,9,.4), inset 0 1px 0 rgba(255,255,255,.22)',
    color: '#fff',
    fontFamily: MONO,
    fontSize: '12px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textShadow: SOFT_HALO,
    animation: 'mytaCinemaKeyPress 3.6s ease-in-out infinite',
  },
  guideMouse: {
    display: 'flex',
    alignItems: 'center',
    filter: 'drop-shadow(0 2px 4px rgba(4,6,9,.45))',
    '& .myta-lmb': {
      fill: palette.accentHi,
      animation: 'mytaCinemaLmbPulse 2.8s ease-in-out infinite',
    },
  },
  tip: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'opacity .35s',
    whiteSpace: 'nowrap',
    [MOBILE]: { display: 'none' },
  },
  tipTag: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '.2em',
    color: palette.linkHi,
    textShadow: HALO,
  },
  tipText: {
    fontSize: '13px',
    color: INK_DIM,
    textShadow: SOFT_HALO,
    '& b': { color: '#fff', fontWeight: 600 },
  },
  bar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '6px',
    zIndex: 12,
    boxShadow: '0 -8px 22px rgba(4,6,9,.3)',
  },
  segTrack: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    background: 'rgba(255,255,255,.26)',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  segFill: {
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, ${palette.buttonBackgroundColor}, ${palette.accentHi})`,
    transformOrigin: 'left center',
    transform: 'scaleX(0)',
    willChange: 'transform',
  },
  head: {
    position: 'absolute',
    top: '50%',
    left: 0,
    marginLeft: '-6px',
    transform: 'translate3d(0, -50%, 0)',
    willChange: 'transform',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#fff',
    zIndex: 2,
    boxShadow: `0 0 0 3px ${withAlpha(palette.accentHi, 0.45)}, 0 0 18px 5px ${
      palette.accentGlow
    }`,
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '9px',
    border: 0,
    borderRadius: '12px',
    background: palette.buttonBackgroundColor,
    color: palette.ctaInk,
    fontFamily: DISP,
    fontSize: '15px',
    fontWeight: 600,
    letterSpacing: '.01em',
    padding: '14px 28px',
    cursor: 'pointer',
    boxShadow: `0 8px 28px ${palette.accentGlow}, 0 1px 0 rgba(255,255,255,.12) inset`,
    transition: 'background .15s, transform .15s, box-shadow .15s',
    animation: 'mytaCinemaRise .4s cubic-bezier(.2,.7,.2,1)',
    '&:hover': {
      background: palette.buttonHoverColor,
      transform: 'translateY(-2px)',
      boxShadow: `0 12px 36px ${palette.accentGlow}`,
    },
    '& svg': { width: '17px', height: '17px' },
  },
});

export const cinemaGlobalStyles = {
  '@keyframes mytaCinemaPulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.4, transform: 'scale(.72)' },
  },
  '@keyframes mytaCinemaRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes mytaCinemaKeyPress': {
    '0%, 14%, 100%': { transform: 'translateY(0)', filter: 'brightness(1)' },
    '5%': {
      transform: 'translateY(2px)',
      filter: 'brightness(1.6)',
      boxShadow: '0 0 0 rgba(4,6,9,.4), inset 0 1px 0 rgba(255,255,255,.22)',
    },
  },
  '@keyframes mytaCinemaLmbPulse': {
    '0%, 100%': { opacity: 0.35 },
    '50%': { opacity: 0.9 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '.myta-cinema *, .myta-cinema *::before, .myta-cinema *::after': {
      animation: 'none !important',
      transition: 'none !important',
    },
  },
};
